import { SelectedInvestmentDTO } from "@models/managed-accounts/entities/step/setup";
import { useTheme } from "@mui/material";
import { SHStack, SHTableCell, SHTableRow } from "@components/design-systems";
import { DeleteButton } from "@components/buttons/delete";
import { FavouritedButtonV2 } from "@components/buttons/favourited-v2";
import { useFormContext } from "react-hook-form";
import { AnalysisFeePortfolioDTO } from "@models/platform-analysis/entities/steps/fee";
import {
  calculateInvestmentValueByWeight,
  calculateInvestmentWeightByValue,
  getInvestmentRowFieldPath,
} from "../../utils";
import { defaultTo } from "lodash";
import { useEffect, useMemo } from "react";
import { RowLabel } from "../investment-label";
import { NumericInputController } from "../numeric-input-controller";

type AnalysisFeePortfolioSetupInvestmentRowProps = {
  item: SelectedInvestmentDTO;
  itemIndex: number;
  calculatedTotal: number;
  disabled?: boolean;

  onDelete: () => void;
  onFavourited: (isFavourited: boolean) => void;
};

export const AnalysisFeePortfolioSetupInvestmentRow = ({
  item,
  itemIndex,
  calculatedTotal,
  disabled = false,
  onDelete,
  onFavourited,
}: AnalysisFeePortfolioSetupInvestmentRowProps) => {
  const { palette } = useTheme();

  const formMethods = useFormContext<AnalysisFeePortfolioDTO>();
  const fieldPaths = getInvestmentRowFieldPath({ itemIndex });

  const investmentValue = formMethods.watch(fieldPaths.value);
  const investmentWeight = formMethods.watch(fieldPaths.weight);
  const isBelowMinimumInvestment = useMemo(
    () =>
      Number(investmentValue) <
      defaultTo(Number(item.minimumInvestmentSize), 0),
    [investmentValue, item.minimumInvestmentSize],
  );

  const handleOnDelete = () => {
    onDelete();
  };

  const handleOnFavourited = () => {
    const newValue = !item.isFavourited;
    formMethods.setValue(fieldPaths.isFavourited, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    onFavourited(newValue);
  };

  const handleOnChangeInvestmentWeight = (value: number) => {
    const investmentValue = calculateInvestmentValueByWeight({
      total: calculatedTotal,
      weight: value,
    });
    formMethods.setValue(fieldPaths.value, investmentValue.toString());
  };

  const handleOnChangeInvestmentValue = (value: number) => {
    const investmentWeight = calculateInvestmentWeightByValue({
      total: calculatedTotal,
      value: value,
    });
    formMethods.setValue(fieldPaths.weight, investmentWeight);
  };

  useEffect(() => {
    const weightNumberValue = defaultTo(Number(investmentWeight), 0);
    if (weightNumberValue !== 0 && investmentValue === 0) {
      handleOnChangeInvestmentWeight(weightNumberValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentWeight, investmentValue]);

  const shouldDisable = useMemo(() => {
    return disabled || item.isMissingInvestment || item.isUnavailable;
  }, [disabled, item.isMissingInvestment, item.isUnavailable]);

  return (
    <SHTableRow
      className={"AnalysisFeePortfolioSetup-table-row"}
      sx={{ width: "100%" }}
    >
      <SHTableCell>
        <SHStack
          direction={"row"}
          alignContent={"center"}
          alignItems={"center"}
          gap={2.5}
        >
          <SHStack width={60}>
            <SHStack
              width={60}
              flexShrink={0}
              direction={"row"}
              gap={1}
              alignItems={"start"}
              justifyContent={"start"}
            >
              <DeleteButton
                disabled={disabled}
                height={24}
                width={24}
                backgroundColor={"transparent"}
                borderColor={palette.common.black}
                iconColor={palette.common.black}
                tabIndex={-1}
                onClick={handleOnDelete}
              />

              {!item.isMissingInvestment && (
                <FavouritedButtonV2
                  disabled={disabled}
                  isFavourited={item.isFavourited}
                  onClick={handleOnFavourited}
                />
              )}
            </SHStack>
          </SHStack>

          <RowLabel
            disabled={shouldDisable}
            label={item.displayName ?? ""}
            code={item.shCode}
            minimumInvestment={item.minimumInvestmentSize}
            isMissingInvestments={item.isMissingInvestment}
            isUnderMinimumInvestment={isBelowMinimumInvestment}
            balanceAvailability={item.balanceAvailability}
            isUnavailable={item.isUnavailable}
          />
        </SHStack>
      </SHTableCell>

      <SHTableCell width={120}>
        <NumericInputController
          disabled={shouldDisable}
          type={"weight"}
          formMethods={formMethods}
          fieldPath={fieldPaths.weight}
          onValueChange={handleOnChangeInvestmentWeight}
        />
      </SHTableCell>

      <SHTableCell width={220}>
        <NumericInputController
          disabled={shouldDisable}
          type={"value"}
          formMethods={formMethods}
          fieldPath={fieldPaths.value}
          onValueChange={handleOnChangeInvestmentValue}
        />
      </SHTableCell>
    </SHTableRow>
  );
};
